import type { AvailabilityWindow } from "domains/hire/components/AvailabilityWindowsField";
import * as z from "zod/mini";

// `24:00` is the end-of-day sentinel the API allows for `end_time` (and `toAvailabilityWindows`
// emits); accept it, but only on the dot — `24:30` is not a time.
const TIME_PATTERN = /^(([01]\d|2[0-3]):[0-5]\d|24:00)$/;

// Times compare as strings: zero-padded `"HH:mm"` sorts chronologically.

/** Touching windows are not an overlap — the service merges those and says so. */
const overlaps = (a: AvailabilityWindow, b: AvailabilityWindow) =>
  a.days.some((day) => b.days.includes(day)) && a.start < b.end && b.start < a.end;

const availabilityWindowSchema = z
  .object({
    days: z.array(z.int().check(z.gte(0), z.lte(6))).check(z.minLength(1, "Pick at least one day")),
    start: z.string().check(z.regex(TIME_PATTERN)),
    end: z.string().check(z.regex(TIME_PATTERN)),
  })
  .check(
    z.refine((window) => window.end > window.start, {
      message: "End time must be after the start time",
      path: ["end"],
    }),
  );

/** No `.min(1)`: never having set availability is not an error state. */
const availabilityWindowsSchema = z.array(availabilityWindowSchema).check(
  z.refine((windows) => windows.every((window, i) => windows.slice(i + 1).every((other) => !overlaps(window, other))), {
    message: "Two windows overlap on the same day",
  }),
);

export const accountAvailabilitySchema = z.object({ windows: availabilityWindowsSchema });
export type AccountAvailabilityForm = z.infer<typeof accountAvailabilitySchema>;

/** Empty windows means no override — inherits the default. */
export const jobAvailabilitySchema = z.object({ windows: availabilityWindowsSchema });
export type JobAvailabilityForm = z.infer<typeof jobAvailabilitySchema>;
